import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { ReportDialogOptions } from '@sentry/browser'
import * as Sentry from '@sentry/react'
import { Primitive } from '@sentry/types'
import SentryErrorBoundary from '../SentryErrorBoundary'
import { User } from '../types'

interface IBugReportingWrapperProps {
  /** Enables error and exception handling */
  errorHandlingEnabled: boolean
  /** The endpoint to which error and exception reports are sent */
  errorHandlingEndpoint: string
  /** A React element to render when the error boundary catches an error */
  fallback: React.ReactElement | Sentry.FallbackRender
  /**
   * If a Sentry User Feedback Widget should be rendered when the Error Boundary catches an error
   *
   * @see {@link https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/user-feedback/}
   */
  showDialog: boolean
  /**
   * Options that are passed into the Sentry User Feedback Widget
   *
   * @see {@link https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/user-feedback/#customizing-the-widget}
   */
  dialogOptions: ReportDialogOptions
  /** The OfferZen service name */
  service: string
  /** The OfferZen environment */
  environment: string
  /** The bundles releaseId, usually the latest short commit SHA */
  releaseId: string
  /** An object containing additional tags to be sent with the report */
  tags: { [key: string]: Primitive }
  /** An object containing additional info about the user who triggered the error */
  user: User
}

function BugReportingWrapper(props: React.PropsWithChildren<IBugReportingWrapperProps>) {
  const {
    errorHandlingEnabled,
    errorHandlingEndpoint,
    fallback,
    showDialog,
    dialogOptions,
    environment,
    service,
    releaseId,
    tags,
    user,
    children,
  } = props

  const userWithIP = {
    ip_address: '{{auto}}',
    ...user,
  }

  const hasInitialised = useRef(false)
  useEffect(() => {
    if (!hasInitialised.current) {
      if (errorHandlingEnabled) {
        Sentry.init({
          dsn: errorHandlingEndpoint,
          environment,
          release: releaseId,
        })

        Sentry.setTags({ service, ...tags })
        Sentry.setUser(userWithIP)
      }

      hasInitialised.current = true
    }
  }, [errorHandlingEnabled, errorHandlingEndpoint, environment, service, releaseId])

  if (errorHandlingEnabled) {
    const boundaryProps = {
      tags, userWithIP, fallback, showDialog, dialogOptions,
    }
    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <SentryErrorBoundary {...boundaryProps}>{children}</SentryErrorBoundary>
    )
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}

const propTypes = {
  /** Enables error and exception handling */
  errorHandlingEnabled: PropTypes.bool,
  /** The endpoint to which error and exception reports are sent */
  errorHandlingEndpoint: PropTypes.string,
  /** A React element to render when the error boundary catches an error */
  fallback: PropTypes.node,
  /**
   * If a Sentry User Feedback Widget should be rendered when the
   * Error Boundary catches an error
   *
   * @see {@link https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/user-feedback/}
   */
  showDialog: PropTypes.bool,
  /**
   * Options that are passed into the Sentry User Feedback Widget
   *
   * @see {@link https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/user-feedback/#customizing-the-widget}
   */
  dialogOptions: PropTypes.shape({
    eventId: PropTypes.string,
    dsn: PropTypes.string,
    user: PropTypes.shape({
      email: PropTypes.string,
      name: PropTypes.string,
    }),
    lang: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    subtitle2: PropTypes.string,
    labelName: PropTypes.string,
    labelEmail: PropTypes.string,
    labelComments: PropTypes.string,
    labelClose: PropTypes.string,
    labelSubmit: PropTypes.string,
    errorGeneric: PropTypes.string,
    errorFormEntry: PropTypes.string,
    successMessage: PropTypes.string,
    onLoad: PropTypes.func,
  }),
  /** The OfferZen service name */
  service: PropTypes.string,
  /** The OfferZen environment */
  environment: PropTypes.string,
  /** The bundles releaseId, usually the latest short commit SHA */
  releaseId: PropTypes.string,
  /** An object containing additional tags to be sent with the report */
  tags: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string, PropTypes.symbol])),
  /** An object containing additional info about the user who triggered the error */
  user: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    ip_address: PropTypes.string,
    role: PropTypes.string,
  }),

  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.elementType,
  ]).isRequired,
}

const defaultProps = {
  errorHandlingEnabled: false,
  errorHandlingEndpoint: null,
  fallback: <p>An error has occurred.</p>,
  showDialog: false,
  dialogOptions: undefined,
  service: undefined,
  environment: 'development',
  releaseId: 'local',
  tags: undefined,
  user: undefined,
}

BugReportingWrapper.propTypes = propTypes
BugReportingWrapper.defaultProps = defaultProps

export default BugReportingWrapper
